import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, CheckCircle, LinkSimple } from "@phosphor-icons/react";

const emptyForm = () => ({ title: "", category: "Study Material", exam_id: "", subject: "", summary: "", url: "" });

export default function AdminStudy() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/study-materials"); setItems(data.items); }
        catch (e) { toast.error("Failed to load"); } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!form.title.trim()) { toast.error("Title required"); return; }
        try {
            if (editing) { await api.put(`/admin/study-materials/${editing}`, form); toast.success("Updated"); }
            else { await api.post("/admin/study-materials", form); toast.success("Created"); }
            setEditing(null); setForm(emptyForm()); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this material?")) return;
        try { await api.delete(`/admin/study-materials/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error("Delete failed"); }
    };

    const openEdit = (m) => {
        setEditing(m.material_id);
        setForm({ title: m.title || "", category: m.category || "Study Material", exam_id: m.exam_id || "", subject: m.subject || "", summary: m.summary || "", url: m.url || "" });
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center">
                <button data-testid="s-new" onClick={() => { setEditing(null); setForm(emptyForm()); }} className="ml-auto btn-primary !py-2 !px-4"><Plus size={16} weight="bold" /> New Material</button>
            </div>

            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold">{editing ? "Edit material" : "New material"}</h3>
                    {editing && <button onClick={() => { setEditing(null); setForm(emptyForm()); }} className="text-xs text-slate-500 flex items-center gap-1"><X size={12} /> Cancel</button>}
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <input placeholder="Title" data-testid="s-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <input placeholder="Exam ID" value={form.exam_id} onChange={(e) => setForm({ ...form, exam_id: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <input placeholder="URL (optional)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm md:col-span-2" />
                </div>
                <textarea placeholder="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} className="w-full px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm resize-y" />
                <button data-testid="s-save" onClick={save} className="ml-auto btn-primary !py-2 !px-4"><CheckCircle size={16} weight="fill" /> {editing ? "Update" : "Create"}</button>
            </div>

            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b-2 border-[#BFD3FF] text-xs uppercase tracking-widest font-bold text-slate-600">{loading ? "Loading…" : `${items.length} material(s)`}</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest text-slate-500"><tr><th className="px-4 py-2 font-bold">Title</th><th className="px-4 py-2 font-bold">Category</th><th className="px-4 py-2 font-bold">Exam</th><th className="px-4 py-2 font-bold">URL</th><th className="px-4 py-2 font-bold text-right w-24">Actions</th></tr></thead>
                        <tbody>
                            {items.map((m) => (
                                <tr key={m.material_id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-4 py-3 text-xs font-medium">{m.title}</td>
                                    <td className="px-4 py-3 text-xs">{m.category}</td>
                                    <td className="px-4 py-3 text-xs uppercase">{m.exam_id || "—"}</td>
                                    <td className="px-4 py-3 text-xs">{m.url ? <a href={m.url} target="_blank" rel="noreferrer" className="text-[#1E3A8A] hover:underline inline-flex items-center gap-1"><LinkSimple size={12} /> link</a> : "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => openEdit(m)} className="p-1.5 text-slate-500 hover:text-[#1E3A8A]"><PencilSimple size={14} /></button>
                                        <button onClick={() => remove(m.material_id)} className="p-1.5 text-slate-500 hover:text-red-600"><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && !loading && (<tr><td colSpan={5} className="text-center py-10 text-slate-400 text-sm">No materials.</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
